import type { Schedule, StreamSpecifier, _Sink } from '@cotera/api';
import {
  ScheduleSchema,
  SinkConfigSchema,
  StreamSpecifierSchema,
} from '@cotera/api';
import { AST, Parser, Relation } from '@cotera/era';
import { z } from 'zod/v3';
import { UserDefinedDimensions } from '../cookbook';
import { MaterializationConfig } from '../materialization';
import { EventStreamDefinition } from './manifest';
import { ScheduledJob, ScheduledJobSchema } from './scheduled-job';

export const DefinitionSchema: z.ZodSchema<{
  rel: AST.RelFR;
  displayName: string;
  hidden: boolean;
}> = z.object({
  rel: Parser.RelSchema,
  displayName: z.string(),
  hidden: z.boolean(),
});

export const MatConfigSchema: z.ZodSchema<{
  schema: string;
  viewName: string;
  schedule: Schedule;
  source: AST.RelFR;
}> = z.object({
  viewName: z.string(),
  schema: z.string(),
  schedule: ScheduleSchema,
  source: Parser.RelSchema,
});

type MatConfig = z.infer<typeof MatConfigSchema>;

export type ManifestShorthand = {
  readonly definitions: Record<
    string,
    | Relation
    | {
        readonly rel: Relation;
        readonly displayName?: string;
        readonly hidden?: boolean;
      }
  >;
  readonly materializations?: {
    readonly schedule: Schedule;
    readonly config: MaterializationConfig;
  }[];
  readonly scheduledJobs?: ScheduledJob[];
  readonly schemas?: { read: string[] };
  readonly alerts?: EventStreamDefinition[];
  readonly udds?: UserDefinedDimensions[];
  readonly eventStreams?: EventStreamDefinition[];
};

const SinkSchema: z.ZodSchema<_Sink> = z.object({
  select: z.record(Parser.IdentifierSchema, Parser.ExprSchema),
  condition: Parser.ExprSchema,
  config: SinkConfigSchema,
});

const EventStreamSchema: z.ZodSchema<{
  identifier: AST.ExprFR;
  entityName: string;
  specifier: StreamSpecifier;
  // name: string;
  onChange: AST.ExprFR;
  rel: AST.RelFR;
  sinks: Record<string, _Sink>;
  when: Schedule;
  batchSize: number;
}> = z.object({
  identifier: Parser.ExprSchema,
  entityName: z.string(),
  specifier: StreamSpecifierSchema,
  onChange: Parser.ExprSchema,
  rel: Parser.RelSchema,
  sinks: z.record(z.string(), SinkSchema),
  when: ScheduleSchema,
  batchSize: z.number(),
});

export const ManifestASTSchema: z.ZodSchema<{
  definitions: Record<string, Definition>;
  materializations: MatConfig[];
  scheduledJobs: ScheduledJob[];
  schemas: { read: string[] };
  eventStreams: EventStreamDefinition[];
}> = z.object({
  definitions: z.record(z.string(), DefinitionSchema),
  eventStreams: z.array(EventStreamSchema),
  materializations: MatConfigSchema.array(),
  scheduledJobs: z.array(ScheduledJobSchema),
  schemas: z.object({ read: z.array(z.string()) }),
});

export type Definition = z.infer<typeof DefinitionSchema>;
export type ManifestAST = z.infer<typeof ManifestASTSchema>;

export const EMPTY_MANIFEST = (): ManifestAST => ({
  definitions: {},
  scheduledJobs: [],
  schemas: { read: [] },
  eventStreams: [],
  materializations: [],
});
